import { useCallback, useMemo } from 'react';

import { useInstantSearch, useRefinementList } from 'react-instantsearch';

import { useTracking } from '@change-corgi/core/react/tracking';

import { INSTANT_SEARCH_TOPIC_FILTER } from 'src/app/pages/search/shared/consts';

type TrackData = { topic_value: string; context: 'landing_topic' | 'topic_filter' };
type Topic = {
	label: string;
	value: string;
	isRefined: boolean;
};

type Result = ModelHookResult<
	{
		topics: Topic[];
		disabled: boolean;
		isInitialLoading: boolean;
	},
	{
		setTopic: (value: string) => void;
		trackTopicsFilterClick: (data: TrackData) => void;
	}
>;

export function useTopicsFilter(): Result {
	const { items, refine, canRefine } = useRefinementList({
		attribute: INSTANT_SEARCH_TOPIC_FILTER,
		limit: 30,
		operator: 'or',
		sortBy: ['count:desc', 'name:asc'],
	});
	const { results, indexUiState } = useInstantSearch();

	const track = useTracking();

	const trackTopicsFilterClick = useCallback(
		(data: TrackData) => {
			void track('search', {
				...data,
				q: indexUiState.query || '',
			});
		},
		[indexUiState, track],
	);

	// The `__isArtificial` flag makes sure not to display the No Results message
	// when no hits have been returned.
	const isInitialLoading = useMemo(() => !!results.__isArtificial, [results]);

	return {
		data: {
			isInitialLoading,
			topics: useMemo(
				() =>
					items.map((item) => ({
						label: item.label,
						value: item.value,
						isRefined: item.isRefined,
					})),
				[items],
			),
			disabled: !canRefine,
		},
		actions: {
			setTopic: useCallback(
				(value: string) => {
					refine(value);
				},
				[refine],
			),
			trackTopicsFilterClick,
		},
	};
}
